import { useEffect, useRef, useState } from 'react'
import posthog from 'posthog-js'
import { toast } from 'ui'
import Sentry from './sentry'
import { decodeJwtPayload } from './decodeJwtPayload'

type SelfAnswer =
  | {
      error?: { type?: string | null } | null
      user?: { organization?: { id?: string | null } | null } | null
    }
  | null
  | undefined

/**
 * Why Self gave a signed-in tab no usable user, or null when it did. Reads
 * `error.type`, never `error` itself: a success carrying `error: {}` arrives
 * as `{ type: null, message: null }`.
 */
export function refusedSessionReason(
  answer: SelfAnswer,
  { requireOrganization }: { requireOrganization: boolean },
): string | null {
  if (!answer) {
    return 'NO_USER_RESPONSE'
  }
  if (answer.error?.type) {
    return answer.error.type
  }
  if (!answer.user) {
    return 'NO_USER'
  }
  if (requireOrganization && !answer.user.organization?.id) {
    return 'NO_ORGANIZATION'
  }
  return null
}

const JUST_SIGNED_IN_SECONDS = 60

export const REFUSED_SESSION_TOAST_ID = 'refusedSession'

export type RefusedSessionSurface =
  | 'dashboard'
  | 'fullscreen'
  | 'dashboard_signed_out'
  | 'fullscreen_signed_out'
  | 'error_link'

function readStoredToken(): string | null {
  try {
    return localStorage.getItem('token')
  } catch {
    // Storage may be unavailable; the session is then unknown.
    return null
  }
}

/** Whether localStorage keeps what is written to it (iOS WebKit quirks). */
function storageRoundTrip(): 'ok' | 'mismatch' | 'threw' {
  const key = 'storageRoundTripProbe'
  const value = String(Math.random())
  try {
    localStorage.setItem(key, value)
    const read = localStorage.getItem(key)
    localStorage.removeItem(key)
    return read === value ? 'ok' : 'mismatch'
  } catch {
    return 'threw'
  }
}

function sessionAgeSeconds(token: string | null): number | null {
  if (!token) {
    return null
  }
  const iat = decodeJwtPayload<{ iat?: number }>(token)?.iat
  return typeof iat === 'number'
    ? Math.max(0, Math.round(Date.now() / 1000 - iat))
    : null
}

function isLiveSession(token: string | null): boolean {
  if (!token) {
    return false
  }
  const claims = decodeJwtPayload<{ expiry?: number; exp?: number }>(token)
  const expiresAtMs =
    claims?.expiry ?? (claims?.exp !== undefined ? claims.exp * 1000 : null)
  return expiresAtMs !== null && Date.now() <= expiresAtMs
}

let endingAccountedFor: string | null = null

export type SessionEndingCause =
  | 'sign_out'
  | 'self_refused'
  | 'session_expired'
  | 'signed_out_elsewhere'

let lastEnding: {
  token: string | null
  cause: SessionEndingCause
  at: number
} | null = null

export type LoginSelf = 'organization' | 'no_organization' | 'rejected'

let lastLogin: { at: number; self: LoginSelf; selfError?: string } | null = null

/** A login this page load completed, and what Self answered right after it. */
export function recordLogin(self: LoginSelf, selfError?: string) {
  lastLogin = { at: Date.now(), self, selfError }
}

function loginContext() {
  if (!lastLogin) {
    return {}
  }
  return {
    ms_since_login: Date.now() - lastLogin.at,
    login_self: lastLogin.self,
    ...(lastLogin.selfError ? { login_self_error: lastLogin.selfError } : {}),
  }
}

// Skips this function's own frame, and the "Error" line V8 puts first
// (WebKit has none).
function callerFrames(stack: string | undefined): string[] {
  return (stack ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line && line !== 'Error')
    .slice(1, 6)
}

export function recordSessionEnding(
  token: string | null,
  cause: SessionEndingCause,
) {
  // A page's refused reads each sign out again with nothing held. Such an
  // ending is news only when a login came after the last one.
  const repeat =
    token === null &&
    lastEnding !== null &&
    (lastLogin === null || lastLogin.at < lastEnding.at)
  lastEnding = { token, cause, at: Date.now() }
  if (repeat) {
    return
  }
  const ageSeconds = sessionAgeSeconds(token)
  try {
    posthog.capture('dashboard_session_forgotten', {
      cause,
      session_held: token !== null,
      session_age_seconds: ageSeconds,
      session_under_a_minute:
        ageSeconds !== null && ageSeconds < JUST_SIGNED_IN_SECONDS,
      path: window.location.pathname,
      stack: callerFrames(new Error().stack),
      ...loginContext(),
    })
  } catch {
    // Reporting must never keep an ended session on screen.
  }
}

const ENDING_BEFORE_MOUNT_MS = 5_000

export function accountForSessionEnding(token: string | null) {
  if (token) {
    endingAccountedFor = token
  }
}

export function reportRefusedSession({
  surface,
  reason,
  detail,
  operation,
  ending,
  token,
  extra,
}: {
  surface: RefusedSessionSurface
  reason: string
  detail?: string
  operation?: string
  ending?: SessionEndingCause | 'unrecorded'
  token: string | null
  extra?: Record<string, string>
}): { justSignedIn: boolean } {
  accountForSessionEnding(token)
  const ageSeconds = sessionAgeSeconds(token)
  const justSignedIn =
    ageSeconds !== null && ageSeconds < JUST_SIGNED_IN_SECONDS
  const tokenStored =
    surface === 'error_link' ? null : readStoredToken() !== null

  if (justSignedIn) {
    const selfAnswered = surface === 'dashboard' || surface === 'fullscreen'
    Sentry.captureMessage(
      selfAnswered
        ? 'Self refused a signed-in session'
        : 'A signed-in session was sent to /login',
      {
        level: 'warning',
        tags: {
          [selfAnswered ? 'self_refusal' : 'session_refusal']: reason,
          refusal_surface: surface,
          ...(operation ? { graphql_operation: operation } : {}),
          ...(ending ? { session_ending: ending } : {}),
          session_under_a_minute: String(justSignedIn),
          ...(tokenStored === null
            ? {}
            : { session_token_present: String(tokenStored) }),
        },
        extra: { sessionAgeSeconds: ageSeconds, detail },
      },
    )
  }
  posthog.capture('dashboard_session_refused', {
    reason,
    surface,
    ...(detail ? { detail } : {}),
    ...(operation ? { operation } : {}),
    ...(ending ? { ending } : {}),
    session_age_seconds: ageSeconds,
    session_under_a_minute: justSignedIn,
    ...(tokenStored === null ? {} : { session_token_present: tokenStored }),
    ...loginContext(),
    ...extra,
  })

  return { justSignedIn }
}

export function reportStaleTokenRefusal({
  code,
  detail,
  operation,
  sentToken,
  storedToken,
}: {
  code: string
  detail?: string
  operation: string
  sentToken: string | null
  storedToken: string
}) {
  posthog.capture('dashboard_session_refused', {
    reason: 'STALE_TOKEN_REFUSED',
    surface: 'error_link',
    refused_code: code,
    ...(detail ? { detail } : {}),
    operation,
    session_token_sent: sentToken !== null,
    session_age_seconds: sessionAgeSeconds(sentToken),
    stored_session_age_seconds: sessionAgeSeconds(storedToken),
  })
}

/**
 * Once per mount, when `reason` is set: tells the merchant why the guard is
 * sending them to /login, and records what Self answered. Reads the stored
 * token, so it must run before the guard's sign-out clears it (an effect
 * declared ahead of that one).
 *
 * A session minted under a minute ago is a login the API refused straight
 * after issuing it. Merchants who hit that in production got in only after
 * reloading the page, so the message offers the reload.
 */
export function useExplainRefusedSession(
  reason: string | null,
  surface: 'dashboard' | 'fullscreen',
) {
  const explained = useRef(false)
  useEffect(() => {
    if (!reason || explained.current) {
      return
    }
    explained.current = true

    const { justSignedIn } = reportRefusedSession({
      surface,
      reason,
      token: readStoredToken(),
    })

    if (justSignedIn) {
      toast.error('We couldn’t finish signing you in', {
        id: REFUSED_SESSION_TOAST_ID,
        description:
          'Reload the page, then log in again. If it keeps happening, contact support@nickel.com.',
        duration: 15_000,
        action: {
          label: 'Reload',
          onClick: () => window.location.reload(),
        },
      })
    } else {
      toast.info('You’ve been signed out', {
        id: REFUSED_SESSION_TOAST_ID,
        description:
          'Your session is no longer valid. Log in again to continue.',
        duration: 15_000,
      })
    }
  }, [reason, surface])
}

function documentLoad(): {
  navigation_type: string | null
  document_from_cache: boolean | null
} {
  try {
    const [navigation] = performance.getEntriesByType('navigation')
    if (!navigation || !('type' in navigation)) {
      return { navigation_type: null, document_from_cache: null }
    }
    const { type, transferSize, decodedBodySize } =
      navigation as PerformanceNavigationTiming
    return {
      navigation_type: type,
      document_from_cache: transferSize === 0 && decodedBodySize > 0,
    }
  } catch {
    return { navigation_type: null, document_from_cache: null }
  }
}

export function useReportSelfUnavailable(
  error: { message: string } | undefined,
  surface: 'dashboard' | 'fullscreen',
) {
  const reported = useRef(false)
  useEffect(() => {
    if (!error || reported.current) {
      return
    }
    reported.current = true
    posthog.capture('dashboard_self_unavailable', {
      surface,
      error: error.message.slice(0, 300),
      ...documentLoad(),
    })
  }, [error, surface])
}

export function useReportSignedOutRedirect(
  signedIn: boolean,
  surface: 'dashboard_signed_out' | 'fullscreen_signed_out',
) {
  const heldToken = useRef<string | null>(null)
  const [mountedAt] = useState(() => Date.now())
  const reported = useRef(false)
  useEffect(() => {
    if (signedIn) {
      heldToken.current = readStoredToken()
      return
    }
    if (reported.current) {
      return
    }
    const stored = readStoredToken()
    if (isLiveSession(stored)) {
      reported.current = true
      reportRefusedSession({
        surface,
        reason: 'STORED_SESSION_NOT_HELD',
        token: stored,
      })
      return
    }
    const endedBeforeMount =
      lastEnding && lastEnding.at >= mountedAt - ENDING_BEFORE_MOUNT_MS
        ? lastEnding.token
        : null
    const lost = heldToken.current ?? endedBeforeMount
    if (lost === null && lastLogin !== null) {
      // A login this page load completed, yet neither this tab nor storage
      // holds a live session, and no ending this tab recorded names it.
      reported.current = true
      reportRefusedSession({
        surface,
        reason: 'STORED_SESSION_MISSING',
        token: stored,
        extra: { storage_round_trip: storageRoundTrip() },
      })
      return
    }
    if (lost === null || lost === endingAccountedFor) {
      return
    }
    const age = sessionAgeSeconds(lost)
    reported.current = true
    reportRefusedSession({
      surface,
      reason:
        age !== null && age < JUST_SIGNED_IN_SECONDS
          ? 'SESSION_LOST_AFTER_LOGIN'
          : 'SESSION_LOST',
      ending: lastEnding?.token === lost ? lastEnding.cause : 'unrecorded',
      token: lost,
    })
  }, [signedIn, surface, mountedAt])
}
